// Thin client for POST /routes/{id}/recalculate; one recalc at a time, the
// tree follow-up lives in useRecalcCompletion.
import { useCallback, useRef, useState } from "react";
import { defaultBunkerPricesPayload } from "@/config/defaults";
import { corePost } from "@/services/coreApi.jsx";
import { useAccessContext } from "@/contexts/AccessContext";
import { useIsCalculating, useRouteStore } from "@/features/route/state/RouteStore.jsx";

export default function useRouteRecalc({ onRecalculated, onError } = {}) {
  const { accessContext, setAccessContext } = useAccessContext();
  // Shared planner flag: blocks waypoint drag/delete and planner runs meanwhile.
  // Only cleared by the recalc when the recalc set it (a planner run may own it).
  const { setIsCalculating } = useRouteStore();
  const isCalculating = useIsCalculating();
  const isCalculatingRef = useRef(isCalculating);
  isCalculatingRef.current = isCalculating;
  const [recalculatingRouteId, setRecalculatingRouteId] = useState(null);
  const [recalcErrorByRouteId, setRecalcErrorByRouteId] = useState({});
  const inFlightRef = useRef(false);

  const clearRecalcError = useCallback((routeId) => {
    setRecalcErrorByRouteId((prev) => {
      if (!(routeId in prev)) return prev;
      const { [routeId]: _dropped, ...rest } = prev;
      return rest;
    });
  }, []);

  const recalculateRoute = useCallback(
    async (target) => {
      const route = target?.route;
      const routeId = Number(route?.id);
      if (route?.source === "draft" || !Number.isFinite(routeId)) return;
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      setRecalculatingRouteId(routeId);
      const ownsFlag = !isCalculatingRef.current;
      if (ownsFlag) setIsCalculating?.(true);
      clearRecalcError(routeId);
      try {
        const response = await corePost(
          `/routes/${routeId}/recalculate`,
          { bunkerPrices: defaultBunkerPricesPayload() },
          accessContext,
          setAccessContext,
          ".core /routes/:id/recalculate error=",
        );
        await onRecalculated?.({ target, ...(response ?? {}) });
      } catch (error) {
        console.error("Route recalc failed:", error);
        const message = error?.message ?? "Recalculation failed.";
        setRecalcErrorByRouteId((prev) => ({ ...prev, [routeId]: message }));
        // The card chip only shows when expanded; the table badge has no chip at all.
        onError?.(message, routeId);
      } finally {
        inFlightRef.current = false;
        setRecalculatingRouteId(null);
        if (ownsFlag) setIsCalculating?.(false);
      }
    },
    [accessContext, setAccessContext, onRecalculated, onError, clearRecalcError, setIsCalculating],
  );

  return {
    recalculateRoute,
    recalculatingRouteId,
    recalcErrorByRouteId,
    clearRecalcError,
  };
}
